import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';

import type { Step } from '@sorare/core/src/components/StepperDialog';
import { StepperDialog } from '@sorare/core/src/components/StepperDialog';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { isType } from '@sorare/core/src/lib/gql';

import { ChooseYourRewardStory } from '#src/components/missions/DailyTask/DecisivePlayerPickerTaskStatus/ChooseYourRewardStory/index.tsx';
import { QuickplayNextLevelStory } from '#src/components/missions/stories/QuickplayNextLevelStory/index.tsx';
import type { RewardUnlockedStory_reward } from '#src/components/missions/stories/RewardUnlockedStory/__generated__/index.graphql.ts';
import { RewardUnlockedStory } from '#src/components/missions/stories/RewardUnlockedStory/index.tsx';
import { useClaimTask } from '#src/components/missions/useClaimTask.tsx';

import type { TaskTrckClaimStory_decisivePlayerPickerTask } from './__generated__/index.graphql.ts';

type Props = {
  task: TaskTrckClaimStory_decisivePlayerPickerTask;
  onClose: () => void;
};
export const TaskTrckClaimStory = ({ task, onClose }: Props) => {
  const [claimTask] = useClaimTask();
  const { sport } = useSportContext();
  const [rewards, setRewards] = useState<RewardUnlockedStory_reward[]>([]);
  const [signedAlternativeRewardConfigs, setSignedAlternativeRewardConfigs] =
    useState<string | undefined>();

  const { isMobileApp, postMessage } = useIsMobileApp();

  if (!isType(task, 'DecisivePlayerPickerTask')) {
    return null;
  }

  const rewardConfigs = task.rewardConfigs || [];
  const alternativeRewardConfigs = task.alternativeRewardConfigs || [];
  const canChooseAlternativeRewardConfigs = alternativeRewardConfigs.length > 0;

  return (
    <StepperDialog
      onClose={() => {
        if (isMobileApp) {
          postMessage('taskClaimed', { taskId: task.id });
        }
        onClose();
      }}
      steps={[
        ...(canChooseAlternativeRewardConfigs
          ? [
              {
                bodyElement: ({ onNext }) => (
                  <ChooseYourRewardStory
                    key="choose-reward-story"
                    rewardConfigs={rewardConfigs}
                    alternativeRewardConfigs={alternativeRewardConfigs}
                    setSignedAlternativeRewardConfigs={
                      setSignedAlternativeRewardConfigs
                    }
                    onConfirm={async () => {
                      const { data } = await claimTask({
                        variables: {
                          taskId: task.id,
                          signedAlternativeRewardConfigs,
                          sport,
                        },
                      });
                      setRewards(data?.claimTask?.rewards || []);
                      onNext();
                    }}
                  />
                ),
              } as Step,
            ]
          : []),
        {
          bodyElement: ({ onNext }) => (
            <RewardUnlockedStory
              key="reward-unlocked-story"
              onClose={onNext}
              rewards={rewards}
              fallbackRewardConfigs={
                signedAlternativeRewardConfigs
                  ? alternativeRewardConfigs[0].rewardConfigs
                  : rewardConfigs
              }
            />
          ),
        },
        {
          bodyElement: ({ onNext }) => (
            <QuickplayNextLevelStory
              key="quickplay-next-level-story"
              onClose={onNext}
              task={task}
            />
          ),
        },
      ]}
    />
  );
};

TaskTrckClaimStory.fragments = {
  taskInterface: gql`
    fragment TaskTrckClaimStory_decisivePlayerPickerTask on DecisivePlayerPickerTask {
      id
      mode
      rewardConfigs {
        id
        ...ChooseYourRewardStory_rewardConfig
        ...RewardUnlockedStory_rewardConfig
      }
      alternativeRewardConfigs {
        ...ChooseYourRewardStory_alternativeRewardConfig
        rewardConfigs {
          id
          ...RewardUnlockedStory_rewardConfig
        }
      }
      ...QuickplayNextLevelStory_taskInterface
    }
    ${ChooseYourRewardStory.fragments.rewardConfig}
    ${ChooseYourRewardStory.fragments.alternativeRewardConfig}
    ${RewardUnlockedStory.fragments.anyRewardConfigInterface}
    ${QuickplayNextLevelStory.fragments.taskInterface}
  ` as TypedDocumentNode<TaskTrckClaimStory_decisivePlayerPickerTask>,
};
